import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LS } from 'src/app/helpers/localStorage.enum';
import { DataService } from 'src/app/services/data/data.service';
import { GroupService } from 'src/app/services/group/group.service';
import { LocationService } from 'src/app/services/location/location.service';
import { OrganisationService } from 'src/app/services/organisation/organisation.service';
import { RoleService } from 'src/app/services/role/role.service';
import {
  getCurrentUser,
  UserService,
} from 'src/app/services/user/user.service';

@Component({
  selector: 'app-create-user',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.sass'],
})
export class CreateComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  userServiceSub: Subscription;
  roles;
  organisations;
  isSuperAdmin = false;
  currentUser;
  groups;
  locations = [];
  selectAllGroups = false;
  selectAllLocations = false;
  submitted: boolean = false;

  unsub$ = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private organisationService: OrganisationService,
    private router: Router,
    private location: Location,
    private userService: UserService,
    private groupService: GroupService,
    private roleService: RoleService,
    private locationService: LocationService,
    private toastr: ToastrService,
    private _t: TranslateService,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.roles = [];
    this.roleService
      .findAll()
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe((res) => {
        const currentUser = getCurrentUser(),
          su = 'SuperAdmin',
          initRolesList = async () => {
            this.currentUser = currentUser;
            this.isSuperAdmin = currentUser.roles.includes(su);

            if (!this.isSuperAdmin) res = res.filter((ele) => ele !== su);

            this.roles = res;

            if (this.isSuperAdmin) {
              const res = await this.organisationService.findAll().toPromise();
              this.organisations = res.data || [];
            } else {
              this.form.value.organisation = currentUser.organisation.id;
            }
          };

        /** This is fallback for users that haven't logged out */
        if (!currentUser?.roles?.length) {
          this.userService
            .findOne(currentUser.user_id)
            .pipe(takeUntil(this.unsub$.asObservable()))
            .subscribe((user) => {
              const { roles } = user;

              if (!roles?.length) return;

              currentUser.roles = roles;
              // localStorage.setItem(LS.currentUser, JSON.stringify(currentUser))
              this.dataService.setLocalStorage(
                LS.currentUser,
                JSON.stringify(currentUser)
              );

              initRolesList();
            });
        } else {
          initRolesList();
        }

        this.initGroupsAndLocations();
      });

    this.form = this.formBuilder.group({
      fullname: ['', Validators.required],
      username: ['', Validators.required],
      password: [{ value: 'abcd1234', disabled: true }, Validators.required],
      role: ['', Validators.required],
      organisation: ['', Validators.required],
      email: ['', Validators.required],
      contact_detail: '',
      job_title: '',
      office_address: '',
      facilityCodeAccessLogin: '',
    });
  }

  async initGroupsAndLocations() {
    if (this.isSuperAdmin) {
      let g = await this.groupService.findAll().toPromise();
      this.groups = g.data;
    } else {
      this.groups = await this.groupService.findOwn().toPromise();
    }
    const locations = await this.locationService.findOwn().toPromise();
    const locationObj = {};
    for (const location of locations) {
      locationObj[location.id] = location;
    }

    for (const location of locations) {
      if (!location.parent) {
        this.locations.push(location);
        for (const child of location.children) {
          if (locationObj[child]) this.locations.push(locationObj[child]);
        }
      }
    }

    for (const group of this.groups) {
      group['member'] = false;
    }
    for (const location of this.locations) {
      location['member'] = false;
    }
  }

  onSubmit() {
    let msg: string;
    let roles = [this.form.value.role];
    if (!this.isSuperAdmin) {
      this.form.patchValue({
        organisation: this.currentUser.organisation?.id,
      });
    }
    // trim whitespace start end
    this.form.value.username = this.form.value.username.trim();
    this.form.value.password = 'abcd1234';
    const vertikaliti = localStorage.getItem('vertikaliti');
    this.form.value.vertikaliti = vertikaliti;

    const selectedGroups = [];
    for (const group of this.groups) {
      if (group.member) selectedGroups.push(group.id);
      // avoid including the new user in the location
      // if the group is already in the location
      for (const location of this.locations) {
        if (location.groups.includes(group.id)) {
          location.member = false;
        }
      }
    }

    const body = { ...this.form.value, groups: selectedGroups, roles: roles };

    this.submitted = true;
    if (this.form.invalid) {
      return;
    }
    this.spinner.show();
    this.userServiceSub = this.userService.create(body).subscribe(
      async (res) => {
        for (const location of this.locations) {
          if (location.member) {
            await this.locationService
              .addMember(location.id, { members: [res.id] })
              .toPromise();
          }
        }
        this.spinner.hide();
        this.router.navigate(['users']);
      },
      ({ error }) => {
        this.spinner.hide();
        if (typeof error?.message === 'object') {
          msg = error.message
            .reduce((results, u) => ((results += u + ', '), results), '')
            .slice(0, -2);
        } else {
          msg = this._t.instant(error.message);
        }
        this.toastr.error(msg, this._t.instant('Error'));
      }
    );
  }

  onChangeAllGroup() {
    this.selectAllGroups = !this.selectAllGroups;
    for (let i = 0; i < this.groups.length; i++) {
      this.onChangeGroup(i, this.selectAllGroups);
    }
  }

  onChangeGroup(groupIndex, value?) {
    this.groups[groupIndex].member = value || !this.groups[groupIndex].member;
    let allGroupsSelected = true;
    for (const group of this.groups) {
      if (!group.member) {
        allGroupsSelected = false;
        break;
      }
    }
    this.selectAllGroups = allGroupsSelected;
    for (const location of this.locations) {
      if (location.groups.includes(this.groups[groupIndex].id)) {
        if (!this.groups[groupIndex].member) {
          location.member = false;
        } else {
          location.member = true;
        }
      }
    }
  }

  onChangeAllLocations() {
    this.selectAllLocations = !this.selectAllLocations;
    if (!this.selectAllLocations) {
      this.selectAllGroups = false;
      for (const group of this.groups) {
        group.member = false;
      }
    }
    for (const location of this.locations) {
      location.member = this.selectAllLocations;
    }
  }

  onChangeLocation(locationIndex) {
    this.locations[locationIndex].member =
      !this.locations[locationIndex].member;
    const selectedLocation = this.locations[locationIndex];
    const isSelected = this.locations[locationIndex].member;

    if (isSelected) {
      let allLocationsSelected = true;
      for (const location of this.locations) {
        if (location.parent == selectedLocation.id) location.member = true;

        if (!location.member) {
          allLocationsSelected = false;
        }

        if (
          this.locations[locationIndex].parent &&
          location.id === this.locations[locationIndex].parent
        )
          location.member = true;
      }
      this.selectAllLocations = allLocationsSelected;
    } else {
      this.selectAllLocations = false;
      if (
        this.locations[locationIndex].children &&
        this.locations[locationIndex].children.length > 0
      )
        for (const location of this.locations) {
          if (this.locations[locationIndex].children.includes(location.id))
            location.member = false;
        }
    }

    for (const group of this.groups) {
      if (
        this.locations[locationIndex].groups.includes(group.id) &&
        !isSelected
      ) {
        group.member = false;
        this.selectAllGroups = false;
      }
    }
  }

  onDiscard() {
    this.location.back();
  }

  ngOnDestroy() {
    if (this.userServiceSub) this.userServiceSub.unsubscribe();
    this.unsub$.next();
    this.unsub$.unsubscribe();
  }
}
